import {
    AnyDepositoryAccount,
    ExternalFinancialEntity,
    isDepositoryAccount,
} from '@common/financialEntities';
import { FieldConfig } from '@common/financialEntities/config';
import {
    isDisconnectedOrError,
    useGoalsFundedByAccount,
} from '@common/react/financialEntities';
import { FC } from 'react';
import TransactionEntriesList from '../cashflow/transactions/TransactionEntriesList';
import {
    EntityViewGrid,
    EntityViewGridProps,
    EntityViewGridSection,
} from '../common/EntityView';
import { FlexCol } from '../common/Flex';
import FinancialEntityDetailExpectedSection from './FinancialEntityDetailExpectedSection';
import FinancialEntityDetailFundingSection from './FinancialEntityDetailFundingSection';
import FinancialEntityDetailInfoSection from './FinancialEntityDetailInfoSection';
import FinancialEntityRelinkSection from './FinancialEntityRelinkSection';

export interface FinancialEntityDetailPanelsProps
    extends Omit<EntityViewGridProps, 'left' | 'right'> {
    financialEntity: ExternalFinancialEntity;
    form: Record<string, any>;
    expectedFields: FieldConfig[];
    infoFields: FieldConfig[];
    linkedFields: string[];
    onFieldChange: (name: string, value: any) => void;
}

const FinancialEntityDetailPanels: FC<FinancialEntityDetailPanelsProps> = ({
    financialEntity: fe,
    form,
    expectedFields,
    infoFields,
    linkedFields,
    onFieldChange,
    ...props
}) => {
    const isDisconnected = fe && isDisconnectedOrError(fe);
    const fundingGoals = useGoalsFundedByAccount(fe);
    const isFundingAccount = fundingGoals.length > 0;

    return (
        <EntityViewGrid
            {...props}
            left={
                <FlexCol>
                    {isDepositoryAccount(fe) && (
                        <FinancialEntityDetailFundingSection
                            fe={fe as AnyDepositoryAccount}
                        />
                    )}
                    {isDisconnected && (
                        <FinancialEntityRelinkSection
                            account={fe}
                            mb={15}
                            isFundingAccount={isFundingAccount}
                        />
                    )}
                    <FinancialEntityDetailInfoSection
                        fe={fe}
                        form={form}
                        fields={infoFields}
                        linkedFields={linkedFields}
                        onFieldChange={onFieldChange}
                    />
                </FlexCol>
            }
            right={
                <FlexCol>
                    <FinancialEntityDetailExpectedSection
                        fe={fe}
                        form={form}
                        fields={expectedFields}
                        onFieldChange={onFieldChange}
                    />
                    {fe.linkStatus !== 'MANUAL' && (
                        <EntityViewGridSection
                            headerProps={{ mb: 2 }}
                            title={'Recent transactions'}
                        >
                            <TransactionEntriesList
                                financialEntityId={fe.id}
                                hoverColor="gray.100"
                            />
                        </EntityViewGridSection>
                    )}
                </FlexCol>
            }
        />
    );
};

export default FinancialEntityDetailPanels;
